import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { Spinner } from './components/ui/Spinner';
import { useAuth } from './context/AuthContext';
import { Analytics } from './pages/Analytics';
import { Campaigns } from './pages/Campaigns';
import { Login } from './pages/Login';
import { Overview } from './pages/Overview';
import { SessionDetail } from './pages/SessionDetail';
import { Sessions } from './pages/Sessions';
import { SiteDetail } from './pages/SiteDetail';
import { Sites } from './pages/Sites';

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  return <>{children}</>;
}

function RequireSuperAdmin({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!user?.isSuperAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <DashboardLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Overview />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="sessions" element={<Sessions />} />
        <Route path="sessions/:sessionId" element={<SessionDetail />} />
        <Route path="campaigns" element={<Campaigns />} />
        <Route
          path="sites"
          element={
            <RequireSuperAdmin>
              <Sites />
            </RequireSuperAdmin>
          }
        />
        <Route
          path="sites/:siteId"
          element={
            <RequireSuperAdmin>
              <SiteDetail />
            </RequireSuperAdmin>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
